import { useContext } from "react";

import { UserContext } from "@/components/User";
import {
  DISPLAY_FORMAT_DATE,
  DISPLAY_FORMAT_TIME_12,
  DISPLAY_FORMAT_TIME_24,
  formatFactory,
  formatPeriod,
  TDate,
} from "@/core/dates";
import { formatNumber, FormatNumberOptions } from "@/core/numbers";
import { formatRate } from "@/data/rate";

export interface UseFormatsReturn {
  formatDate: (date?: TDate | null) => string;
  formatDateTime: (date?: TDate | null) => string;
  formatTime: (date?: TDate | null) => string;
  formatNumber: (n: number | undefined, options?: FormatNumberOptions) => string;
  formatPeriod: (start: TDate, end: TDate) => string;
  formatRate: (rate: number, currency?: string, unit?: "h" | "d", options?: FormatNumberOptions) => string;
  formatCost: (cost: number, currency: string, options?: FormatNumberOptions) => string;
  dateFormat: string;
  dateTimeFormat: string;
  timeFormat: string;
}

const DEFAULT_NUMBER_FORMAT = "SV";

export const useFormats = (): UseFormatsReturn => {
  const User = useContext(UserContext);

  const userZone = User.sessionUser?.settings.zone;
  const userDateFormat = User.sessionUser ? User.sessionUser.settings.dateFormat.parameter.format : DISPLAY_FORMAT_DATE;
  const userTimeFormat =
    User.sessionUser && User.sessionUser.settings.timeFormat.code === "_12"
      ? DISPLAY_FORMAT_TIME_12
      : DISPLAY_FORMAT_TIME_24;
  const userDateTimeFormat = `${userDateFormat} ${userTimeFormat}`;

  const formatUserNumber = formatNumber(
    User.sessionUser ? User.sessionUser.settings.numberFormat.code : DEFAULT_NUMBER_FORMAT,
  );
  const formatUserDate = formatFactory(userDateFormat, userZone);
  const formatUserDateTime = formatFactory(userDateTimeFormat, userZone);
  const formatUserTime = formatFactory(userTimeFormat, userZone);

  return {
    formatNumber: formatUserNumber,
    formatRate: (rate, currency, unit, options) => formatRate(formatUserNumber(rate, options), currency, unit),
    formatCost: (cost, currency, options) => `${formatUserNumber(cost, options)} ${currency}`,
    formatDate: formatUserDate,
    formatDateTime: formatUserDateTime,
    formatTime: formatUserTime,
    formatPeriod: formatPeriod(userDateFormat),
    dateFormat: userDateFormat,
    dateTimeFormat: userDateTimeFormat,
    timeFormat: userTimeFormat,
  };
};
